import { format } from 'date-fns'
import  actionsApiHttpService from './actionsApiHttpService'
import { RequestParams } from '../models/enhancement'
import { OpenAbsenceResponse } from '../types/open-absence'
import { WFMAbsenceActionedPayload } from '../types/absence'
import { GetMyActionsWithPagination } from '../types/my-actions-with-pagination'
import { PendingSurveysResponse, SurveyResponsePayload } from '../types/survey'

export interface WfmAbsence {
  id: number
  wfmScheduleId: string
  absenceStartDate: string
  absenceEndDate: string
  scheduleStartDate: string
  scheduleEndDate: string
  created: string
  actionedAt?: string
  notes: string
  actioned: boolean
  lastActionedBy: string
  employeeId: number
  absenceId?: number
  absenceDayId?: number
  shiftString: string
  absenceLength: number
  isPartDayAbsence: boolean
  absenceTypeId: number
  absenceReasonTypeId: number
}

export interface UnlockWfmAbsenceResponse {
  result: boolean
}

export interface LockWfmAbsenceResponse extends UnlockWfmAbsenceResponse {
  lockedBy?: string
}

export interface LinkWfmPlaceholderToAbsenceAndDayResponse {
  success: boolean
  errorMessage?: string
}

const employeeEndpoints = {
  getOpenAbsencesByEmployeeId: async (employeeId?: number): Promise<OpenAbsenceResponse[]> =>
    actionsApiHttpService.get(`/employees/${employeeId}/open-absences`),
}

const wfmEndpoints = {
  getWfmAbsenceById: async (wfmAbsenceId: number): Promise<WfmAbsence> =>
    actionsApiHttpService.get(`/wfm/absences/${wfmAbsenceId}`),
  tryGetLockForWfmAbsencePlaceholder: async (
    wfmAbsenceId: number
  ): Promise<LockWfmAbsenceResponse> =>
    actionsApiHttpService.get(`/wfm/absences/lock/${wfmAbsenceId}`),
  unlockWfmAbsencePlaceholder: async (wfmAbsenceId: number): Promise<UnlockWfmAbsenceResponse> =>
    actionsApiHttpService.post(`/wfm/absences/unlock/${wfmAbsenceId}`),
  linkWfmPlaceholderToAbsence: async (
    body: WFMAbsenceActionedPayload
  ): Promise<LinkWfmPlaceholderToAbsenceAndDayResponse> =>
    actionsApiHttpService.post(`/wfm/absences/LinkWfmPlaceholderToAbsence`, body),
}

const myActionsEndpoints = {
  getManagerActions: async (params: RequestParams): Promise<GetMyActionsWithPagination> => {
    let requestURL =
      `my-actions` +
      `?DirectReportees=${params.directReportees}` +
      `&DateFrom=${format(params.dateFrom, 'yyyy-MM-dd')}` +
      `&DateTo=${format(params.dateTo, 'yyyy-MM-dd')}` +
      `${params.departmentId ? `&DepartmentId=${params.departmentId}` : ``}` +
      `${params.departmentTeamId ? `&DepartmentTeamId=${params.departmentTeamId}` : ``}` +
      `&RequestStatusId=${params.requestStatusId >= 0 ? params.requestStatusId : 6}`

    if (params.pageSize !== undefined) {
      requestURL += `&pageSize=${params.pageSize}`
    }

    if (params.page !== undefined) {
      requestURL += `&pageNumber=${params.page}`
    }

    return actionsApiHttpService.get(requestURL)
  },
}

interface QrCodeResponse {
  redirectUrlId: string
  additionalInfo: string
  redirectAddress: string
}

type UserAnalyticsEventType =
  | 'PageLoad'
  | 'UIClickEvent'

interface UserAnalyticsEvent {
  eventType: UserAnalyticsEventType
  eventName: string
  fullPagePath: string
  hostname: string
}

const qrCodes = {
  get: async (id: string): Promise<QrCodeResponse> =>
    actionsApiHttpService.get(`/qr/${id}`)
}

const userAnalytics = {
  postEvent: async (
    eventType: UserAnalyticsEventType,
    eventName: string,
    fullPagePath: string,
    hostname: string
  ): Promise<void> => {
    const payload: UserAnalyticsEvent = { eventType, eventName, fullPagePath, hostname }
    return actionsApiHttpService.post('/useranalytics/events', payload)
  },

  postSurveyResponse: async (
    surveyId: number,
    response: string | null,
    comments: string
  ): Promise<void> => {
    const payload: SurveyResponsePayload = { surveyId, response, comments }
    return actionsApiHttpService.post('/useranalytics/surveys/responses', payload)
  },

  getPendingSurveys: async (): Promise<PendingSurveysResponse> =>
    actionsApiHttpService.get('/useranalytics/surveys/pending')
}

const actionsService = {
  wfm: { ...wfmEndpoints },
  myActions: { ...myActionsEndpoints },
  employee: { ...employeeEndpoints },
  qr: {...qrCodes},
  userAnalytics
}

export default actionsService
export type { QrCodeResponse, UserAnalyticsEventType }